import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/DeliveryDining';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatIcon from '@mui/icons-material/Chat';
import { clinicAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import ClinicExtendedFeatures from './ClinicExtendedFeatures';
import AccountSettingsPanel from '../AccountSettingsPanel';
import { MessagingConversationListItem, MessagingMessageRow } from '../MessagingUI';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import { useMessagingInbox } from '../../hooks/useMessagingInbox';
import { formatLocalDate, formatLocalDateTime, localCalendarYearMonth } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import DashboardSectionLoader from '../DashboardSectionLoader';
import GradientSpinner from '../GradientSpinner';
import Pagination from '../Pagination';
import Modal from '../Modal';
import ConfirmModal from '../ConfirmModal';
import '../../App.css';

/** YYYY-MM → short month label (UTC) */
function formatAnalyticsMonth(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return ym || '—';
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const PICKUP_STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  declined: 'Declined',
  assigned: 'Assigned',
  'in-progress': 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

function pickupStatusLabel(status) {
  if (!status) return '—';
  return PICKUP_STATUS_LABELS[status] || status;
}

const ClinicDashboard = ({ activeTab: propTab }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError, showInfo } = useNotify();
  const [pickupSuccessModal, setPickupSuccessModal] = useState(null);
  const [pickupRequests, setPickupRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [demographicsStats, setDemographicsStats] = useState(null);
  const [demographicsStatsLoading, setDemographicsStatsLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [demoAnalyticsMonth, setDemoAnalyticsMonth] = useState(() =>
    typeof window !== 'undefined' ? localCalendarYearMonth() : ''
  );
  const [demoAnalyticsDay, setDemoAnalyticsDay] = useState('');
  const [demoAnalyticsLabId, setDemoAnalyticsLabId] = useState('');
  const [demoAnalyticsPanel, setDemoAnalyticsPanel] = useState('all');
  const [demoPanelOptions, setDemoPanelOptions] = useState([]);
  const [demoFilteredStats, setDemoFilteredStats] = useState(null);
  const [demoFilteredLoading, setDemoFilteredLoading] = useState(false);
  const dashboard = { pickupRequests, reports };
  const [activeTab, setActiveTab] = useState(propTab || 'overview');
  const [formData, setFormData] = useState({});
  const [pickupRows, setPickupRows] = useState([{ patientName: '', patientDOB: '', patientAge: '', address: '', doctorName: '', panel: '', tests: '' }]);
  const [preferredPhlebotomistId, setPreferredPhlebotomistId] = useState('');
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [clinicDetailModal, setClinicDetailModal] = useState(null);
  const [cancelPickupConfirm, setCancelPickupConfirm] = useState(null);

  const {
    conversations,
    messages: chatMessages,
    selectedConvId: selectedConv,
    setSelectedConvId: setSelectedConv,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading: messagingLoading
  } = useMessagingInbox({
    enabled: activeTab === 'messaging',
    filterConversation: (c) => c.otherParty?.role === 'lab'
  });
  
  // Pagination states
  const [pickupPage, setPickupPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);
  const [pickupSearch, setPickupSearch] = useState('');
  const [pickupStatusFilter, setPickupStatusFilter] = useState('');
  const [reportsSearch, setReportsSearch] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    setPickupPage(1);
  }, [pickupSearch, pickupStatusFilter]);

  useEffect(() => {
    setReportsPage(1);
  }, [reportsSearch]);

  useEffect(() => {
    if (propTab) setActiveTab(propTab);
  }, [propTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, pickupsRes, reportsRes, labsRes] = await Promise.all([
        clinicAPI.getDashboard(),
        clinicAPI.getPickupRequests(),
        clinicAPI.getReports(),
        clinicAPI.getLabs()
      ]);
      if (dashRes.data?.pickupRequests) setPickupRequests(dashRes.data.pickupRequests);
      if (dashRes.data?.reports) setReports(dashRes.data.reports);
      if (pickupsRes.data?.requests) setPickupRequests(pickupsRes.data.requests);
      if (reportsRes.data?.reports) setReports(reportsRes.data.reports);
      if (labsRes.data?.labs) setLabs(labsRes.data.labs);
    } catch (err) {
      console.error('Clinic fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    setDemographicsStatsLoading(true);
    setAnalyticsLoading(true);
    (async () => {
      try {
        const [statsRes, analyticsRes] = await Promise.all([
          clinicAPI.getDemographicsStats(),
          clinicAPI.getAnalytics()
        ]);
        if (!cancelled) {
          setDemographicsStats(statsRes.data);
          setAnalytics(analyticsRes.data);
        }
      } catch (err) {
        console.error('Analytics tab:', err);
        if (!cancelled) {
          setDemographicsStats(null);
          setAnalytics(null);
        }
      } finally {
        if (!cancelled) {
          setDemographicsStatsLoading(false);
          setAnalyticsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    setDemoFilteredLoading(true);
    (async () => {
      try {
        const params = {};
        if (demoAnalyticsMonth) params.month = demoAnalyticsMonth;
        if (demoAnalyticsDay) params.day = demoAnalyticsDay;
        if (demoAnalyticsLabId) params.labId = demoAnalyticsLabId;
        if (demoAnalyticsPanel && demoAnalyticsPanel !== 'all') params.panel = demoAnalyticsPanel;
        const { data } = await clinicAPI.getDemographicsAnalytics(params);
        if (!cancelled) setDemoFilteredStats(data);
      } catch (err) {
        console.error('Demographics analytics:', err);
        if (!cancelled) setDemoFilteredStats(null);
      } finally {
        if (!cancelled) setDemoFilteredLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, demoAnalyticsMonth, demoAnalyticsDay, demoAnalyticsLabId, demoAnalyticsPanel]);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await clinicAPI.getDemographicsPanelOptions();
        if (!cancelled) setDemoPanelOptions(Array.isArray(data?.options) ? data.options : []);
      } catch (err) {
        console.error('Demographics panel options:', err);
        if (!cancelled) setDemoPanelOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const mergedDemoPanelOptions = useMemo(() => {
    const out = [...demoPanelOptions];
    if (demoAnalyticsPanel && demoAnalyticsPanel !== 'all' && !out.some((o) => o === demoAnalyticsPanel)) {
      out.push(demoAnalyticsPanel);
      out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return out;
  }, [demoPanelOptions, demoAnalyticsPanel]);

  const handleAddPickupRow = () => {
    setPickupRows(prev => [...prev, { patientName: '', patientDOB: '', patientAge: '', address: '', doctorName: '', panel: '', tests: '' }]);
  };

  const handleUpdatePickupRow = (index, field, value) => {
    setPickupRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleRemovePickupRow = (index) => {
    if (pickupRows.length > 1) setPickupRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitPickupRequest = async (e) => {
    e.preventDefault();
    const labId = formData.labId;
    const preferredDate = formData.preferredDate || new Date().toISOString().split('T')[0];
    const preferredTime = formData.preferredTime;
    const urgency = formData.urgency || 'routine';
    const validRows = pickupRows.filter(r => r.patientName?.trim());
    if (!validRows.length) {
      showInfo('Add at least one patient.');
      return;
    }
    if (!labId) {
      showInfo('Select an associated lab.');
      return;
    }
    const requests = validRows.map((row) => ({
      patientName: row.patientName,
      patientDOB: row.patientDOB || undefined,
      patientAge: row.patientAge ? parseInt(row.patientAge, 10) : undefined,
      address: row.address,
      doctorName: row.doctorName,
      panel: row.panel || undefined,
      tests: row.tests ? (() => { try { return JSON.parse(row.tests); } catch { return []; } })() : []
    }));
    try {
      const { data } = await clinicAPI.submitPickupRequest({
        labId,
        preferredDate,
        preferredTime,
        urgency,
        preferredPhlebotomistId: preferredPhlebotomistId || undefined,
        requests
      });
      setPickupRequests(prev => [...(data.requests || []), ...prev]);
      const labLabel = labs.find((l) => l._id === labId)?.labName || labs.find((l) => l._id === labId)?.name || 'Lab';
      setPickupSuccessModal({
        count: requests.length,
        labLabel,
        patients: validRows.map((r) => ({ name: r.patientName, dob: r.patientDOB }))
      });
      showSuccess(data.message || `${requests.length} pickup request(s) submitted.`);
      setFormData({});
      setPickupRows([{ patientName: '', patientDOB: '', patientAge: '', address: '', doctorName: '', panel: '', tests: '' }]);
      setPreferredPhlebotomistId('');
      setShowPickupModal(false);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to submit pickup request');
    }
  };

  const handleCancelPickupRequest = (requestId) => {
    setCancelPickupConfirm({ requestId });
  };

  const openClinicPickupDetail = (req) => setClinicDetailModal({ type: 'pickup', data: req });
  const openClinicReportDetail = (report) => setClinicDetailModal({ type: 'report', data: report });

  const filteredPickupsForTable = useMemo(() => {
    let rows = dashboard.pickupRequests || [];
    if (pickupStatusFilter) {
      rows = rows.filter((r) => r.status === pickupStatusFilter);
    }
    return filterBySearch(rows, pickupSearch, (r) => [
      r.patientName,
      r.panel,
      r.status,
      r.urgency,
      r.lab?.labName,
      r.lab?.name,
      ...(r.tests || []).map((t) => t.testName)
    ]);
  }, [dashboard.pickupRequests, pickupSearch, pickupStatusFilter]);

  const filteredReportsForTable = useMemo(
    () =>
      filterBySearch(dashboard.reports || [], reportsSearch, (r) => [
        r.reportNumber,
        r.patientName,
        r.panel,
        r.lab?.labName,
        r.lab?.name,
        ...(r.tests || []).map((t) => t.testName)
      ]),
    [dashboard.reports, reportsSearch]
  );

  const pickupStartIndex = (pickupPage - 1) * itemsPerPage;
  const pickupEndIndex = pickupStartIndex + itemsPerPage;
  const paginatedPickups = filteredPickupsForTable.slice(pickupStartIndex, pickupEndIndex);
  const totalPickupPages = Math.max(
    1,
    Math.ceil(filteredPickupsForTable.length / itemsPerPage)
  );

  const reportsStartIndex = (reportsPage - 1) * itemsPerPage;
  const reportsEndIndex = reportsStartIndex + itemsPerPage;
  const paginatedReports = filteredReportsForTable.slice(reportsStartIndex, reportsEndIndex);
  const totalReportsPages = Math.max(1, Math.ceil(filteredReportsForTable.length / itemsPerPage));

  const getPageTitle = () => {
    switch (activeTab) {
      case 'pickup-requests': return 'Pickup Requests';
      case 'reports': return 'Reports';
      case 'analytics': return 'Analytics';
      case 'messaging': return 'Messages';
      case 'service-requests': return 'Lab requests';
      case 'demographics': return 'Demographics';
      case 'audit-logs': return 'Audit log';
      case 'settings': return 'Account';
      default: return 'Overview';
    }
  };

  return (
    <div>
      {activeTab !== 'overview' && (
        <h1 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          margin: '0 0 8px 0',
          paddingTop: '0'
        }}>
          {getPageTitle()}
        </h1>
      )}
      <ClinicExtendedFeatures activeTab={activeTab} labs={labs} />

      <Modal
        isOpen={!!clinicDetailModal}
        onClose={() => setClinicDetailModal(null)}
        title={clinicDetailModal?.type === 'pickup' ? 'Pickup request' : 'Report'}
        wide
      >
        {clinicDetailModal?.type === 'pickup' && clinicDetailModal.data && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Patient</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.patientName}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Date of birth</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.patientDOB ? formatLocalDate(clinicDetailModal.data.patientDOB) : '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.lab?.labName || clinicDetailModal.data.lab?.name || '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Panel</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.panel || '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Preferred date</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(clinicDetailModal.data.preferredDate)}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Urgency</p>
                <span className={`badge ${
                  clinicDetailModal.data.urgency === 'urgent' ? 'badge-danger' :
                  clinicDetailModal.data.urgency === 'stat' ? 'badge-warning' : 'badge-info'
                }`}>
                  {clinicDetailModal.data.urgency}
                </span>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Status</p>
                <span className={`badge ${
                  clinicDetailModal.data.status === 'completed' ? 'badge-success' :
                  clinicDetailModal.data.status === 'declined' ? 'badge-danger' : 'badge-info'
                }`}>
                  {clinicDetailModal.data.status}
                </span>
              </div>
            </div>
            {clinicDetailModal.data.tests?.length > 0 && (
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Tests</p>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{clinicDetailModal.data.tests.map((t) => t.testName).join(', ')}</p>
              </div>
            )}
          </div>
        )}
        {clinicDetailModal?.type === 'report' && clinicDetailModal.data && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Report #</p>
                <p style={{ margin: 0, fontFamily: 'monospace', fontWeight: '500' }}>{clinicDetailModal.data.reportNumber}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Patient</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.patientName}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Panel</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.panel || '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{clinicDetailModal.data.lab?.labName || clinicDetailModal.data.lab?.name || '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Date</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(clinicDetailModal.data.reportDate)}</p>
              </div>
            </div>
            {clinicDetailModal.data.tests?.length > 0 && (
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Tests</p>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {clinicDetailModal.data.tests.map((t, idx) => (
                    <div key={idx} style={{ padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                      <p style={{ margin: 0, fontWeight: '500' }}>{t.testName}{t.result ? ` — ${t.result}` : ''}</p>
                      {t.testCode && <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{t.testCode}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!pickupSuccessModal}
        onClose={() => setPickupSuccessModal(null)}
        title="Pickup request confirmed"
      >
        {pickupSuccessModal && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>
              Submitted <strong>{pickupSuccessModal.count}</strong> request{pickupSuccessModal.count === 1 ? '' : 's'} to <strong>{pickupSuccessModal.labLabel}</strong>. The lab will review and update status.
            </p>
            <div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Patients</p>
              <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.6 }}>
                {pickupSuccessModal.patients.map((p, i) => (
                  <li key={i}>
                    <strong>{p.name}</strong>
                    {p.dob ? ` · DOB ${formatLocalDate(p.dob)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setPickupSuccessModal(null)}>
              OK
            </button>
          </div>
        )}
      </Modal>

      {loading && activeTab === 'overview' && <DashboardSectionLoader />}
      {activeTab === 'overview' && dashboard && !loading && (
        <>
        <div className="quick-actions">
          <Link to="/dashboard?tab=pickup-requests" className="quick-action-btn">
            <span className="quick-action-icon"><LocalShippingIcon sx={{ fontSize: 28 }} /></span>
            <span>Submit Pickup</span>
          </Link>
          <Link to="/dashboard?tab=reports" className="quick-action-btn">
            <span className="quick-action-icon"><AssessmentIcon sx={{ fontSize: 28 }} /></span>
            <span>View Reports</span>
          </Link>
          <Link to="/dashboard?tab=analytics" className="quick-action-btn">
            <span className="quick-action-icon"><BarChartIcon sx={{ fontSize: 28 }} /></span>
            <span>Analytics</span>
          </Link>
          <Link to="/dashboard?tab=messaging" className="quick-action-btn">
            <span className="quick-action-icon"><ChatIcon sx={{ fontSize: 28 }} /></span>
            <span>Messages</span>
          </Link>
        </div>
        <div className="grid grid-2">
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Pickup Requests</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
              {dashboard.pickupRequests?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Total requests</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Reports</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
              {dashboard.reports?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Total reports</p>
          </div>
        </div>
        </>
      )}

      {activeTab === 'pickup-requests' && (
        <div>
          <Modal
            isOpen={showPickupModal}
            onClose={() => { setShowPickupModal(false); setPickupRows([{ patientName: '', patientDOB: '', patientAge: '', address: '', doctorName: '', panel: '', tests: '' }]); }}
            title="Submit Pickup Request(s)"
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Request specimen pickups from your lab. Add multiple patients to submit in one go. Lab can assign a phlebotomist.</p>
            <form onSubmit={handleSubmitPickupRequest}>
              <div className="form-group">
                <label className="form-label">Lab</label>
                <select className="form-select" value={formData.labId || ''} onChange={(e) => setFormData({ ...formData, labId: e.target.value })} required>
                  <option value="">Select an associated lab</option>
                  {labs.map((l) => (
                    <option key={l._id} value={l._id}>{l.labName || l.name}</option>
                  ))}
                </select>
                {!labs.length && <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>No labs associated. Ask your lab to add your clinic.</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.preferredDate || ''}
                  onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Urgency</label>
                <select className="form-select" value={formData.urgency || 'routine'} onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}>
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent</option>
                  <option value="stat">Stat</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Time</label>
                <input type="text" className="form-input" value={formData.preferredTime || ''} onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })} placeholder="e.g. 10:00 AM" />
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Phlebotomist (optional)</label>
                <input type="text" className="form-input" value={preferredPhlebotomistId} onChange={(e) => setPreferredPhlebotomistId(e.target.value)} placeholder="Request specific phlebotomist from lab by ID or name" />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Ask your lab for available phlebotomists. Enter ID to request a specific one.</p>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '20px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <label className="form-label" style={{ margin: 0 }}>Patients</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    {pickupRows.filter((r) => r.patientName?.trim()).length} patient{pickupRows.filter((r) => r.patientName?.trim()).length === 1 ? '' : 's'} in this request · add as many as you need
                  </p>
                </div>
                <button type="button" className="btn btn-primary" style={{ padding: '8px 14px', fontSize: '0.875rem' }} onClick={handleAddPickupRow}>
                  + Add patient
                </button>
              </div>
              {pickupRows.map((row, idx) => (
                <div key={idx} style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '12px', background: 'var(--bg-tertiary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Patient {idx + 1}</span>
                    {pickupRows.length > 1 && <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => handleRemovePickupRow(idx)}>Remove</button>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Patient Name</label>
                    <input type="text" className="form-input" value={row.patientName} onChange={(e) => handleUpdatePickupRow(idx, 'patientName', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-input" value={row.patientDOB} onChange={(e) => handleUpdatePickupRow(idx, 'patientDOB', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Age (optional)</label>
                    <input type="number" className="form-input" value={row.patientAge} onChange={(e) => handleUpdatePickupRow(idx, 'patientAge', e.target.value)} min={0} placeholder="Patient age" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <input type="text" className="form-input" value={row.address} onChange={(e) => handleUpdatePickupRow(idx, 'address', e.target.value)} placeholder="Street, city, state, zip" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Doctor Name</label>
                    <input type="text" className="form-input" value={row.doctorName} onChange={(e) => handleUpdatePickupRow(idx, 'doctorName', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Recommended Panel</label>
                    <input type="text" className="form-input" value={row.panel} onChange={(e) => handleUpdatePickupRow(idx, 'panel', e.target.value)} placeholder="e.g. Urology, CBC, Metabolic Panel" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tests</label>
                    <textarea className="form-textarea" value={row.tests} onChange={(e) => handleUpdatePickupRow(idx, 'tests', e.target.value)} placeholder='[{"testName": "CBC", "testCode": "CBC001"}]' rows={2} />
                  </div>
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Attach media (optional)</label>
                <input type="file" accept="image/*,.pdf" multiple className="form-input" style={{ padding: '8px' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary">Submit {pickupRows.filter(r => r.patientName?.trim()).length || 1} Request(s)</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowPickupModal(false)}>Cancel</button>
              </div>
            </form>
          </Modal>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Pickup Requests</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowPickupModal(true)}>
                  Submit Pickup Request
                </button>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  {dashboard.pickupRequests?.length || 0}
                </span>
              </div>
            </div>
            <TableToolbar
              search={pickupSearch}
              onSearchChange={setPickupSearch}
              placeholder="Search patient, lab, panel, status, tests…"
              resultCount={filteredPickupsForTable.length}
              totalCount={dashboard.pickupRequests?.length || 0}
            >
              <select
                className="form-select"
                value={pickupStatusFilter}
                onChange={(e) => setPickupStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="pending">pending</option>
                <option value="confirmed">confirmed</option>
                <option value="assigned">assigned</option>
                <option value="in-progress">in-progress</option>
                <option value="completed">completed</option>
                <option value="declined">declined</option>
                <option value="cancelled">cancelled</option>
              </select>
            </TableToolbar>
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>DOB</th>
                    <th>Lab</th>
                    <th>Panel</th>
                    <th>Tests</th>
                    <th>Preferred Date</th>
                    <th>Urgency</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPickups.map((req) => (
                    <tr key={req._id}>
                      <td style={{ fontWeight: '500' }}>{req.patientName}</td>
                      <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{req.patientDOB ? formatLocalDate(req.patientDOB) : '—'}</td>
                      <td>{req.lab?.labName}</td>
                      <td>{req.panel || '—'}</td>
                      <td>
                        <span style={{ fontSize: '0.875rem' }}>
                          {req.tests?.map(t => t.testName).join(', ')}
                        </span>
                      </td>
                      <td>{formatLocalDate(req.preferredDate)}</td>
                      <td>
                        <span className={`badge ${
                          req.urgency === 'urgent' ? 'badge-danger' :
                          req.urgency === 'stat' ? 'badge-warning' : 'badge-info'
                        }`}>
                          {req.urgency}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          req.status === 'completed' ? 'badge-success' :
                          req.status === 'in-progress' ? 'badge-info' :
                          req.status === 'assigned' ? 'badge-warning' : 'badge-info'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }} onClick={() => openClinicPickupDetail(req)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
              {paginatedPickups.map((req) => (
                <div key={req._id} className="table-mobile-card-item">
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Patient</span>
                    <span className="table-mobile-card-value" style={{ fontWeight: '500' }}>{req.patientName}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">DOB</span>
                    <span className="table-mobile-card-value">{req.patientDOB ? formatLocalDate(req.patientDOB) : '—'}</span>
                  </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Panel</span>
                  <span className="table-mobile-card-value">{req.panel || '—'}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Lab</span>
                  <span className="table-mobile-card-value">{req.lab?.labName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Tests</span>
                    <span className="table-mobile-card-value" style={{ fontSize: '0.875rem' }}>
                      {req.tests?.map(t => t.testName).join(', ')}
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Preferred Date</span>
                    <span className="table-mobile-card-value">{formatLocalDate(req.preferredDate)}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Urgency</span>
                    <span className="table-mobile-card-value">
                      <span className={`badge ${
                        req.urgency === 'urgent' ? 'badge-danger' :
                        req.urgency === 'stat' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {req.urgency}
                      </span>
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Status</span>
                    <span className="table-mobile-card-value">
                      <span className={`badge ${
                        req.status === 'completed' ? 'badge-success' :
                        req.status === 'declined' ? 'badge-danger' :
                        req.status === 'confirmed' ? 'badge-success' :
                        req.status === 'in-progress' ? 'badge-info' :
                        req.status === 'assigned' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {req.status}
                      </span>
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Actions</span>
                    <span className="table-mobile-card-value">
                      <button type="button" className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }} onClick={() => openClinicPickupDetail(req)}>
                        View
                      </button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={pickupPage}
              totalPages={totalPickupPages}
              onPageChange={setPickupPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredPickupsForTable.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Reports</h2>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {dashboard.reports?.length || 0}
            </div>
          </div>
          <TableToolbar
            search={reportsSearch}
            onSearchChange={setReportsSearch}
            placeholder="Search report #, patient, lab, panel, tests…"
            resultCount={filteredReportsForTable.length}
            totalCount={dashboard.reports?.length || 0}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Report Number</th>
                  <th>Patient</th>
                  <th>Panel</th>
                  <th>Lab</th>
                  <th>Date</th>
                  <th>Tests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((report) => (
                  <tr key={report._id}>
                    <td style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</td>
                    <td>{report.patientName}</td>
                    <td>{report.panel || '—'}</td>
                    <td>{report.lab?.labName || report.lab?.name}</td>
                    <td>{formatLocalDate(report.reportDate)}</td>
                    <td>
                      <span style={{ fontSize: '0.875rem' }}>
                        {report.tests?.map(t => t.testName).join(', ')}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }} onClick={() => openClinicReportDetail(report)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-mobile-card">
            {paginatedReports.map((report) => (
              <div key={report._id} className="table-mobile-card-item">
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Report #</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Patient</span>
                  <span className="table-mobile-card-value">{report.patientName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Panel</span>
                  <span className="table-mobile-card-value">{report.panel || '—'}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Lab</span>
                  <span className="table-mobile-card-value">{report.lab?.labName || report.lab?.name}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Date</span>
                  <span className="table-mobile-card-value">{formatLocalDate(report.reportDate)}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Tests</span>
                  <span className="table-mobile-card-value" style={{ fontSize: '0.875rem' }}>
                    {report.tests?.map(t => t.testName).join(', ')}
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Actions</span>
                  <span className="table-mobile-card-value">
                    <button type="button" className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }} onClick={() => openClinicReportDetail(report)}>
                      View
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            currentPage={reportsPage}
            totalPages={totalReportsPages}
            onPageChange={setReportsPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredReportsForTable.length}
          />
        </div>
      )}

      {activeTab === 'analytics' && (
        <>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '8px' }}>Tests sent — filtered totals</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px', maxWidth: '920px', lineHeight: 1.45 }}>
              Pick the specimen calendar month (defaults to this month), optional day, and a lab you work with (or all linked labs). The headline and panel cards count only rows dated in that month (e.g. January 2026). Clear the month for all-time totals (server-capped). Panel category lists distinct Panel values from demographics linked to your clinic.
            </p>
            <div className="lab-demo-filters__grid lab-demo-filters__grid--primary" style={{ maxWidth: '960px' }}>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-analytics-month">Month (specimen date)</label>
                <input
                  id="clinic-demo-analytics-month"
                  type="month"
                  className="form-input"
                  value={demoAnalyticsMonth}
                  onChange={(e) => setDemoAnalyticsMonth(e.target.value)}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '6px 0 0', lineHeight: 1.35 }}>
                  {demoAnalyticsMonth
                    ? <>Showing {formatAnalyticsMonth(demoAnalyticsMonth)} only. Clear for all dates.</>
                    : <>No month filter — all specimen dates (capped).</>}
                </p>
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-analytics-day">Single day (optional)</label>
                <input
                  id="clinic-demo-analytics-day"
                  type="date"
                  className="form-input"
                  value={demoAnalyticsDay}
                  onChange={(e) => setDemoAnalyticsDay(e.target.value)}
                />
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-analytics-lab">Lab you work with</label>
                <select
                  id="clinic-demo-analytics-lab"
                  className="form-input"
                  value={demoAnalyticsLabId}
                  onChange={(e) => setDemoAnalyticsLabId(e.target.value)}
                >
                  <option value="">All linked labs</option>
                  {(labs || []).map((l) => (
                    <option key={l._id} value={l._id}>{l.labName || l.name || l.email || l._id}</option>
                  ))}
                </select>
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-analytics-panel">Panel category</label>
                <select
                  id="clinic-demo-analytics-panel"
                  className="form-input"
                  value={demoAnalyticsPanel}
                  onChange={(e) => setDemoAnalyticsPanel(e.target.value)}
                  title="Options come from Panel values in demographics linked to your clinic"
                >
                  <option value="all">All panels</option>
                  {mergedDemoPanelOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
              {demoFilteredLoading ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Calculating…</p>
              ) : (
                <>
                  <p style={{ fontSize: '2rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--logo-lime-green)' }}>
                    {(demoFilteredStats?.testsCounted ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.45 }}>
                    Individual tests (ordered tests field) · {(demoFilteredStats?.specimenRows ?? 0).toLocaleString()} specimen rows match filters
                    {demoAnalyticsPanel !== 'all' ? ' and selected panel' : ''}
                  </p>
                  {demoFilteredStats?.panelBreakdown?.panels?.length > 0 && (
                    <div style={{ marginTop: '18px' }}>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.45 }}>
                        Tests by panel for
                        {demoAnalyticsMonth ? ` ${formatAnalyticsMonth(demoAnalyticsMonth)}` : ' the selected filters'}
                        {demoAnalyticsDay ? ` · day ${demoAnalyticsDay}` : ''}
                        {demoAnalyticsLabId
                          ? ` · ${(labs || []).find((l) => l._id === demoAnalyticsLabId)?.labName || (labs || []).find((l) => l._id === demoAnalyticsLabId)?.name || 'one lab'}`
                          : ''}
                        {' '}
                        (not affected by the panel dropdown above).
                      </p>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
                          gap: '10px'
                        }}
                      >
                        {demoFilteredStats.panelBreakdown.panels.map((block) => (
                          <div
                            key={block.key}
                            style={{
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '10px 12px'
                            }}
                          >
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={block.label}
                            >
                              {block.label}
                            </div>
                            <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                              {(block.testsCounted ?? 0).toLocaleString()} tests
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              {(block.specimenRows ?? 0).toLocaleString()} specimens
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="grid grid-3" style={{ gap: '24px' }}>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=demographics')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=demographics');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Tests (demographics)</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {demographicsStatsLoading ? '…' : (demographicsStats?.testsCounted ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.45 }}>
              {(demographicsStats?.specimenRows ?? 0).toLocaleString()} rows linked to your clinic · Click to open demographics
            </p>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=pickup-requests')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=pickup-requests');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Pickup Requests by Month</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {analyticsLoading ? '…' : (analytics?.pickupTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Total pickups (all time) · By month when each request was created · Click to open pickup requests
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.pickupsByMonth || []).map((d, i) => (
                <div key={`${d.month}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{formatAnalyticsMonth(d.month)}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
              {!analyticsLoading && !(analytics?.pickupsByMonth || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No pickup requests yet.</p>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-lime-green)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-lime-green) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=reports')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=reports');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Reports Generated</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-lime-green)' }}>
              {analyticsLoading ? '…' : (analytics?.reportTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Total reports (all time) · By report date (fallback: created) · Click to open reports
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.reportsByMonth || []).map((d, i) => (
                <div key={`${d.month}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{formatAnalyticsMonth(d.month)}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>{d.count}</span>
                </div>
              ))}
              {!analyticsLoading && !(analytics?.reportsByMonth || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No reports yet.</p>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=pickup-requests')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=pickup-requests');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Pickup Status</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {analyticsLoading ? '…' : (analytics?.pickupTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Current mix across all pickups · Click to filter and manage in pickup requests
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.pickupsByStatus || []).map((d, i) => (
                <div key={`${d.status}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{pickupStatusLabel(d.status)}</span>
                  <span className="badge badge-info">{d.count}</span>
                </div>
              ))}
              {!analyticsLoading && !(analytics?.pickupsByStatus || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No pickup requests yet.</p>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {activeTab === 'settings' && (
        <AccountSettingsPanel
          user={user}
          refreshUser={refreshUser}
          onMessage={(m) => {
            if (!m) return;
            const s = String(m).trim();
            if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
            else showSuccess(s);
          }}
        />
      )}

      {activeTab === 'messaging' && (
        <div className="card messaging-card">
          <h2 className="card-title">Messages (with your Lab)</h2>
          <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="form-label" style={{ margin: 0 }}>New message</label>
            <select
              className="form-input"
              style={{ maxWidth: '280px' }}
              defaultValue=""
              aria-label="Start conversation with a lab"
              onChange={(e) => {
                const v = e.target.value;
                if (v) startConversation(v);
                e.target.value = '';
              }}
            >
              <option value="">Select a lab…</option>
              {(labs || []).map((lab) => (
                <option key={lab._id} value={lab._id}>{lab.labName || lab.name || lab.email || lab._id}</option>
              ))}
            </select>
            {messagingLoading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <GradientSpinner size={22} />
                Loading…
              </span>
            )}
          </div>
          <div className="messaging-layout">
            <div className="messaging-conversation-list">
              {conversations.map((conv) => (
                <MessagingConversationListItem
                  key={conv._id}
                  conv={conv}
                  selectedConvId={selectedConv}
                  onSelect={setSelectedConv}
                  title={userDisplayLabel(conv.otherParty)}
                />
              ))}
            </div>
            <div className="messaging-thread">
              {selectedConv ? (
                <>
                  <div className="messaging-messages">
                    {chatMessages.map((msg) => (
                      <MessagingMessageRow key={msg._id} msg={msg} meUser={user} formatLocalDateTime={formatLocalDateTime} />
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="messaging-composer">
                    <input type="file" accept="image/*,.pdf" multiple style={{ maxWidth: '120px', padding: '4px', fontSize: '0.75rem' }} title="Attach" disabled />
                    <input type="text" className="form-input" placeholder="Type a message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!messageText.trim()}>Send</button>
                  </form>
                </>
              ) : (
                <div className="messaging-empty">Select a conversation with your Lab</div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!cancelPickupConfirm}
        onCancel={() => setCancelPickupConfirm(null)}
        onConfirm={() => {
          const id = cancelPickupConfirm?.requestId;
          setCancelPickupConfirm(null);
          if (!id) return;
          setPickupRequests((prev) => prev.filter((req) => req._id !== id));
          showSuccess('Pickup request cancelled.');
        }}
        title="Cancel pickup request"
        message="Are you sure you want to cancel this pickup request?"
        confirmLabel="Yes, cancel"
        danger
      />
    </div>
  );
};

export default ClinicDashboard;
